/**
 * Privacy Sandbox Topics API integration (Phase 8.2).
 *
 * `document.browsingTopics()` returns up to three topic IDs the browser has
 * observed for the visitor. Only available in Chromium with the API enabled
 * and the publisher has opted-in via Permissions-Policy: browsing-topics=().
 *
 * We call it best-effort from the SDK's first-party context and forward
 * whatever the browser returns into the ad-server bid request. Empty array
 * outside Chromium / when the API is disabled — costs zero bytes thanks
 * to the JSON omitempty on the server end.
 *
 * Returned shape mirrors the spec: each Topic is an object with the IAB
 * taxonomy id (`topic`) plus the version triple. The server forwards into
 * OpenRTB `user.ext.browsing_topics` for bidders that already understand
 * the spec.
 *
 * The browser API is async — we expose a Promise-returning collector with
 * a hard 60 ms timeout so a hung call cannot delay ad rendering.
 */

export interface Topic {
  /** IAB content-taxonomy v1 / v2 id. */
  topic: number;
  /** Chrome publishes `chrome.1` today. */
  version: string;
  configVersion: string;
  modelVersion: string;
  taxonomyVersion: string;
}

interface TopicsDocument extends Document {
  browsingTopics?: () => Promise<Topic[]>;
}

const TIMEOUT_MS = 60;

/**
 * Best-effort Topics pickup. Always resolves within {@link TIMEOUT_MS}; on
 * any failure / non-Chromium environment / disabled API returns [].
 */
export async function collectTopics(): Promise<Topic[]> {
  if (typeof document === 'undefined') return [];
  const d = document as TopicsDocument;
  if (typeof d.browsingTopics !== 'function') return [];

  let settled = false;
  return new Promise<Topic[]>((resolve) => {
    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      resolve([]);
    }, TIMEOUT_MS);

    try {
      d.browsingTopics!()
        .then((topics) => {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          resolve(Array.isArray(topics) ? topics.filter(isValidTopic) : []);
        })
        .catch(() => {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          resolve([]);
        });
    } catch {
      // synchronous throw (e.g. SecurityError when called from a sandboxed
      // iframe without permission). Resolve with [] silently.
      settled = true;
      clearTimeout(timer);
      resolve([]);
    }
  });
}

function isValidTopic(t: unknown): t is Topic {
  if (!t || typeof t !== 'object') return false;
  const r = t as Record<string, unknown>;
  return typeof r.topic === 'number' && Number.isFinite(r.topic) && r.topic > 0;
}
